import { RefreshCw, ListTodo, TrendingUp, TrendingDown, ArrowUpDown, ChevronLeft, ChevronRight, History, Zap, Trophy, Clock } from 'lucide-react';
/**
 * Order History
 *
 * Two-tab view:
 *   1. Trade History — persisted trades from Firestore decision logs (permanent)
 *   2. Active Orders — live broker orders from Alpaca/Tradier (ephemeral)
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { ClickableTicker } from '../TradingViewModal';
import { decisionsApi, titanApi } from '../../lib/api';

// --- Types ---

/** A completed trade lifecycle from Firestore decision logs */
interface TradeRecord {
  summary: {
    symbol: string;
    pattern_name: string;
    side: string;
    entry_price: number;
    exit_price: number;
    pnl: number;
    pnl_pct: number;
    hold_minutes: number;
    exit_reason: string;
    quantity: number;
    entry_time: string | null;
    exit_time: string | null;
    asset_class: string;
    broker: string;
    slippage_pct: number;
    fees: number;
    status?: string;
  };
  signal: any;
  execution: any;
  exit: any;
}

interface TradeStats {
  total_trades: number;
  wins: number;
  losses: number;
  win_rate: number;
  total_pnl: number;
  avg_hold_minutes: number;
}

/** A live broker order from Alpaca/Tradier */
interface BrokerOrder {
  id: string;
  symbol: string;
  side: string;
  quantity: number;
  order_type: string;
  status: string;
  filled_qty: number;
  filled_avg_price: number | null;
  limit_price: number | null;
  stop_price: number | null;
  submitted_at: string | null;
  filled_at: string | null;
  created_at: string | null;
  broker: string | null;
}

// --- Helpers ---

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(value);

const formatPercent = (value: number): string =>
  `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;

const formatDateTime = (timestamp: string | null): string => {
  if (!timestamp) return '-';
  return new Date(timestamp).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York',
  });
};

const formatDuration = (minutes: number): string => {
  if (minutes < 1) return '<1m';
  if (minutes < 60) return `${Math.round(minutes)}m`;
  if (minutes < 1440) return `${(minutes / 60).toFixed(1)}h`;
  return `${(minutes / 1440).toFixed(1)}d`;
};

// Map asset classes to broker names for filtering
const ASSET_CLASS_BROKERS: Record<string, string[]> = {
  stocks: ['alpaca', 'tradier'],
  forex: ['oanda'],
  futures: ['tradier'],
  crypto: ['coinbase'],
};

const PERIODS = [
  { label: '7d', value: 7 },
  { label: '30d', value: 30 },
  { label: '90d', value: 90 },
];

// --- Components ---

type SortField = 'exit_time' | 'symbol' | 'pnl' | 'pnl_pct' | 'hold_minutes';
type SortDirection = 'asc' | 'desc';

interface OrderHistoryProps {
  assetClass?: string;
}

const OrderHistory: React.FC<OrderHistoryProps> = ({ assetClass }) => {
  const [tab, setTab] = useState('history');

  // --- Trade History state ---
  const [trades, setTrades] = useState<TradeRecord[]>([]);
  const [tradeStats, setTradeStats] = useState<TradeStats | null>(null);
  const [tradeLoading, setTradeLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [sortField, setSortField] = useState<SortField>('exit_time');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [tradePage, setTradePage] = useState(0);
  const [tradeRowsPerPage, setTradeRowsPerPage] = useState(25);

  // --- Active Orders state ---
  const [brokerOrders, setBrokerOrders] = useState<BrokerOrder[]>([]);
  const [brokerLoading, setBrokerLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // --- Fetch Trade History (Firestore-persisted) ---
  const fetchTradeHistory = useCallback(async () => {
    setTradeLoading(true);
    try {
      const data = await decisionsApi.trades({ days, limit: 500, asset_class: assetClass }) as any;
      setTrades(data.trades || []);
      setTradeStats(data.stats || null);
    } catch {
      // non-fatal
    } finally {
      setTradeLoading(false);
    }
  }, [days, assetClass]);

  // --- Fetch Active Orders (live broker) ---
  const fetchBrokerOrders = useCallback(async () => {
    setBrokerLoading(true);
    try {
      const data = await titanApi.orders({ limit: 100, status_filter: statusFilter !== 'all' ? statusFilter : undefined }) as any;
      let orders = data.orders || [];
      if (assetClass) {
        const brokers = ASSET_CLASS_BROKERS[assetClass];
        if (brokers) orders = orders.filter((o: BrokerOrder) => o.broker && brokers.includes(o.broker.toLowerCase()));
      }
      setBrokerOrders(orders);
    } catch {
      // non-fatal
    } finally {
      setBrokerLoading(false);
    }
  }, [statusFilter, assetClass]);

  // Auto-fetch on mount and period change
  useEffect(() => { fetchTradeHistory(); }, [fetchTradeHistory]);
  useEffect(() => {
    if (tab === 'active') {
      fetchBrokerOrders();
      const interval = setInterval(fetchBrokerOrders, 10000);
      return () => clearInterval(interval);
    }
  }, [tab, fetchBrokerOrders]);

  // --- Trade History: sort & paginate ---
  const sortedTrades = useMemo(() => {
    return [...trades].sort((a, b) => {
      const aS = a.summary, bS = b.summary;
      let aVal: any, bVal: any;
      switch (sortField) {
        case 'exit_time': aVal = aS.exit_time ? new Date(aS.exit_time).getTime() : 0; bVal = bS.exit_time ? new Date(bS.exit_time).getTime() : 0; break;
        case 'symbol': aVal = aS.symbol; bVal = bS.symbol; break;
        case 'pnl': aVal = aS.pnl; bVal = bS.pnl; break;
        case 'pnl_pct': aVal = aS.pnl_pct; bVal = bS.pnl_pct; break;
        case 'hold_minutes': aVal = aS.hold_minutes; bVal = bS.hold_minutes; break;
        default: return 0;
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [trades, sortField, sortDirection]);

  const paginatedTrades = useMemo(() => {
    const start = tradePage * tradeRowsPerPage;
    return sortedTrades.slice(start, start + tradeRowsPerPage);
  }, [sortedTrades, tradePage, tradeRowsPerPage]);

  const totalTradePages = Math.ceil(sortedTrades.length / tradeRowsPerPage);

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('desc'); }
    setTradePage(0);
  };

  const SortableHeader: React.FC<{ field: SortField; children: React.ReactNode; className?: string }> = ({ field, children, className }) => (
    <TableHead className={className}>
      <button className="flex items-center gap-1 hover:text-foreground transition-colors" onClick={() => handleSort(field)}>
        {children}
        <ArrowUpDown className={`w-3 h-3 ${sortField === field ? 'opacity-100' : 'opacity-40'}`} />
      </button>
    </TableHead>
  );

  return (
    <div className="space-y-4">
      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <TabsList>
            <TabsTrigger value="history" className="gap-1.5">
              <History className="h-4 w-4" />
              Trade History
              {tradeStats && <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0 h-4">{tradeStats.total_trades}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="active" className="gap-1.5">
              <Zap className="h-4 w-4" />
              Active Orders
              {brokerOrders.length > 0 && <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0 h-4">{brokerOrders.length}</Badge>}
            </TabsTrigger>
          </TabsList>

          {/* Period selector (trade history only) */}
          {tab === 'history' && (
            <div className="flex items-center gap-2">
              <div className="inline-flex items-center rounded-md bg-muted p-0.5">
                {PERIODS.map((p) => (
                  <button
                    key={p.value}
                    onClick={() => { setDays(p.value); setTradePage(0); }}
                    className={`px-2.5 py-1 text-xs font-medium rounded-sm transition-colors ${
                      days === p.value ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={fetchTradeHistory} disabled={tradeLoading}>
                <RefreshCw className={`w-4 h-4 ${tradeLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          )}
        </div>

        {/* ====== TRADE HISTORY TAB ====== */}
        <TabsContent value="history" className="space-y-4">
          {/* Stats Cards */}
          {tradeStats && tradeStats.total_trades > 0 && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Card>
                <CardContent className="p-3 md:p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <ListTodo className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Trades</span>
                  </div>
                  <span className="text-xl font-bold">{tradeStats.total_trades}</span>
                  <span className="text-xs text-muted-foreground block">
                    {(tradeStats as any).open > 0 ? `${(tradeStats as any).open} open · ` : ''}{tradeStats.wins}W / {tradeStats.losses}L
                  </span>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 md:p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Trophy className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Win Rate</span>
                  </div>
                  <span className={`text-xl font-bold ${tradeStats.win_rate >= 50 ? 'text-emerald-500' : 'text-rose-500'}`}>
                    {tradeStats.win_rate}%
                  </span>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 md:p-4">
                  <div className="flex items-center gap-2 mb-1">
                    {tradeStats.total_pnl >= 0 ? <TrendingUp className="w-4 h-4 text-emerald-500" /> : <TrendingDown className="w-4 h-4 text-rose-500" />}
                    <span className="text-xs text-muted-foreground">Total P&L</span>
                  </div>
                  <span className={`text-xl font-bold ${tradeStats.total_pnl >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                    {formatCurrency(tradeStats.total_pnl)}
                  </span>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 md:p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Avg Hold</span>
                  </div>
                  <span className="text-xl font-bold">{formatDuration(tradeStats.avg_hold_minutes)}</span>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Loading */}
          {tradeLoading && trades.length === 0 && (
            <div className="space-y-3 p-4">
              {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          )}

          {/* Empty State */}
          {!tradeLoading && trades.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <History className="w-12 h-12 text-muted-foreground/30 mb-3" />
              <span className="text-lg font-medium">No completed trades</span>
              <span className="text-sm text-muted-foreground mt-1">
                Completed trades will appear here with full entry &rarr; exit details and P&L.
              </span>
              <span className="text-xs text-muted-foreground mt-2">
                Try expanding the period to 90 days.
              </span>
            </div>
          )}

          {/* Trades Table */}
          {trades.length > 0 && (
            <div className="rounded-lg border bg-card">
              <div className="overflow-x-auto scrollbar-thin">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableHeader field="symbol">Symbol</SortableHeader>
                      <TableHead>Side</TableHead>
                      <TableHead>Qty</TableHead>
                      <TableHead>Entry</TableHead>
                      <TableHead>Exit</TableHead>
                      <SortableHeader field="pnl" className="text-right">P&L</SortableHeader>
                      <SortableHeader field="pnl_pct" className="text-right">P&L %</SortableHeader>
                      <TableHead>Reason</TableHead>
                      <TableHead>Bot</TableHead>
                      <SortableHeader field="hold_minutes">Hold</SortableHeader>
                      <SortableHeader field="exit_time">Closed</SortableHeader>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedTrades.map((trade, idx) => {
                      const s = trade.summary;
                      const isOpen = s.status === 'open' || s.exit_reason === 'OPEN';
                      const isBrokerHistory = s.status === 'broker_history';
                      const isWin = s.pnl > 0;
                      return (
                        <TableRow key={idx} className={isOpen ? 'bg-blue-500/5' : ''}>
                          <TableCell className="font-medium">
                            <ClickableTicker symbol={s.symbol} />
                          </TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                              (s.side || '').toLowerCase() === 'buy' ? 'bg-emerald-500/10 text-emerald-700' : 'bg-rose-500/10 text-rose-700'
                            }`}>
                              {(s.side || '').toUpperCase()}
                            </span>
                          </TableCell>
                          <TableCell>{s.quantity}</TableCell>
                          <TableCell className="font-mono text-xs">{formatCurrency(s.entry_price)}</TableCell>
                          <TableCell>
                            {isOpen ? (
                              <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20 text-[10px]">OPEN</Badge>
                            ) : isBrokerHistory ? (
                              <span className="text-xs text-muted-foreground font-mono">{formatCurrency(s.entry_price)}</span>
                            ) : (
                              <div className="flex items-center gap-1 font-mono text-xs">
                                {isWin ? <TrendingUp className="w-3 h-3 text-emerald-500" /> : <TrendingDown className="w-3 h-3 text-rose-500" />}
                                {formatCurrency(s.exit_price)}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {(isOpen || isBrokerHistory) ? (
                              <span className="text-xs text-muted-foreground">—</span>
                            ) : (
                              <span className={`font-medium ${isWin ? 'text-emerald-500' : s.pnl < 0 ? 'text-rose-500' : 'text-muted-foreground'}`}>
                                {formatCurrency(s.pnl)}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {(isOpen || isBrokerHistory) ? (
                              <span className="text-xs text-muted-foreground">—</span>
                            ) : (
                              <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium ${
                                isWin ? 'bg-emerald-500/10 text-emerald-600' : s.pnl < 0 ? 'bg-rose-500/10 text-rose-600' : 'bg-muted text-muted-foreground'
                              }`}>
                                {formatPercent(s.pnl_pct)}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {isOpen ? (
                              <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20 text-[10px]">Holding</Badge>
                            ) : isBrokerHistory ? (
                              <Badge variant="secondary" className="text-[10px]">Filled</Badge>
                            ) : (
                              <Badge variant="secondary" className="text-[10px] capitalize">
                                {s.exit_reason?.replace(/_/g, ' ') || '-'}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {s.pattern_name ? (
                              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-blue-500/10 text-blue-700 max-w-[120px] truncate" title={s.pattern_name}>
                                {s.pattern_name}
                              </span>
                            ) : '-'}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-xs">{formatDuration(s.hold_minutes)}</TableCell>
                          <TableCell>
                            <span className="text-xs text-muted-foreground whitespace-nowrap">{formatDateTime(s.exit_time)}</span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {/* Pagination */}
              {totalTradePages > 1 && (
                <div className="flex items-center justify-between text-sm text-muted-foreground p-3 border-t">
                  <div className="flex items-center gap-2">
                    <span className="hidden sm:inline">Rows:</span>
                    <select
                      value={tradeRowsPerPage}
                      onChange={(e) => { setTradeRowsPerPage(parseInt(e.target.value, 10)); setTradePage(0); }}
                      className="bg-transparent border rounded px-1 py-0.5 text-sm"
                    >
                      {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs">{tradePage * tradeRowsPerPage + 1}-{Math.min((tradePage + 1) * tradeRowsPerPage, sortedTrades.length)} of {sortedTrades.length}</span>
                    <Button variant="ghost" size="icon" className="h-8 w-8" disabled={tradePage === 0} onClick={() => setTradePage(tradePage - 1)}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" disabled={tradePage >= totalTradePages - 1} onClick={() => setTradePage(tradePage + 1)}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* ====== ACTIVE ORDERS TAB ====== */}
        <TabsContent value="active" className="space-y-4">
          <div className="rounded-lg border bg-card">
            <div className="p-3 md:p-4 border-b">
              <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5" />
                  <span className="font-semibold">Live Broker Orders</span>
                  <Badge variant="secondary">{brokerOrders.length}</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex rounded-md border overflow-hidden">
                    {['all', 'open', 'closed'].map((f) => (
                      <button
                        key={f}
                        onClick={() => setStatusFilter(f)}
                        className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                          statusFilter === f ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                        }`}
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={fetchBrokerOrders} disabled={brokerLoading}>
                    <RefreshCw className={`w-4 h-4 ${brokerLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>
            </div>

            {/* Loading */}
            {brokerLoading && brokerOrders.length === 0 && (
              <div className="p-4 space-y-3">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            )}

            {/* Empty */}
            {!brokerLoading && brokerOrders.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Zap className="w-12 h-12 text-muted-foreground/30 mb-3" />
                <span className="text-lg font-medium">No active broker orders</span>
                <span className="text-sm text-muted-foreground mt-1">
                  Live orders from Alpaca/Tradier will appear here in real-time.
                </span>
              </div>
            )}

            {/* Table */}
            {brokerOrders.length > 0 && (
              <div className="overflow-x-auto scrollbar-thin">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Symbol</TableHead>
                      <TableHead>Side</TableHead>
                      <TableHead>Qty</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Fill Price</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Broker</TableHead>
                      <TableHead>Submitted</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {brokerOrders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell className="font-medium">
                          <ClickableTicker symbol={order.symbol} />
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                            order.side === 'buy' ? 'bg-emerald-500/10 text-emerald-700' : 'bg-rose-500/10 text-rose-700'
                          }`}>
                            {order.side.toUpperCase()}
                          </span>
                        </TableCell>
                        <TableCell>{order.filled_qty || order.quantity}</TableCell>
                        <TableCell className="capitalize text-xs">{order.order_type}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {order.filled_avg_price ? formatCurrency(order.filled_avg_price) : order.limit_price ? formatCurrency(order.limit_price) : '-'}
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
                            order.status === 'filled' ? 'bg-emerald-500/10 text-emerald-700' :
                            order.status === 'new' || order.status === 'accepted' ? 'bg-blue-500/10 text-blue-700' :
                            order.status === 'partially_filled' ? 'bg-amber-500/10 text-amber-700' :
                            'bg-zinc-500/10 text-zinc-500'
                          }`}>
                            {order.status}
                          </span>
                        </TableCell>
                        <TableCell>
                          {order.broker ? (
                            <Badge variant="secondary" className="text-[10px]">{order.broker.toUpperCase()}</Badge>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatDateTime(order.submitted_at || order.created_at)}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground text-center">
            Live orders refresh every 10 seconds. For permanent trade records, see the Trade History tab.
          </p>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default OrderHistory;
